import { GameService, GameServiceImpl } from '../application/services/GameService';
import { YouTubePlayer } from './YouTubePlayer';
import { MemoryContent, TransitionContent, Question, MediaContent } from '../domain/entities/GameConfig';
import { YouTubeClip } from '../domain/entities/MediaContent';
import gsap from 'gsap';

export class Game {
    private gameService: GameService;
    private container: HTMLElement;
    private youtubePlayer: YouTubePlayer | null = null;
    private dragState: {
        dragging: HTMLElement | null;
        slots: HTMLElement[];
    } = { dragging: null, slots: [] };
    private isGameStarted: boolean = false;

    constructor(containerId: string) {
        this.gameService = new GameServiceImpl();
        const container = document.getElementById(containerId);
        if (!container) {
            throw new Error(`Container with id ${containerId} not found`);
        }
        this.container = container;
        this.renderLandingPage();
    }

    private renderLandingPage(): void {
        this.container.innerHTML = `
            <div class="min-h-screen bg-gradient-to-br from-valentine-navy to-valentine-deep-red p-4 sm:p-8">
                <div class="w-[95%] max-w-7xl mx-auto bg-white/95 backdrop-blur-sm rounded-xl p-8 sm:p-16 text-center shadow-lg border border-valentine-gold/10">
                    <h1 class="text-4xl sm:text-6xl font-display font-light text-valentine-burgundy mb-6 tracking-wide animate-glow">A Chronicle For Us</h1>
                    <p class="text-lg sm:text-xl font-elegant text-valentine-charcoal mb-12 tracking-wider uppercase">For Kristina, Made by yours, truly.</p>
                    <button 
                        class="w-48 px-8 sm:px-12 py-4 text-lg rounded-none transition-all transform hover:scale-105 bg-valentine-burgundy text-white uppercase tracking-widest font-light hover:bg-valentine-deep-red shadow-lg hover:shadow-xl"
                        onclick="window.startGame()"
                    >
                        Enter
                    </button>
                </div>
            </div>
        `;

        (window as any).startGame = this.startGame.bind(this);
    }

    private async startGame(): Promise<void> {
        this.isGameStarted = true;
        await this.initialize();
    }

    async initialize(): Promise<void> {
        if (!this.isGameStarted) return;
        
        await this.gameService.initializeGame();
        this.render();
        this.setupEventListeners();
        this.initializeParticles();
    }

    private initializeParticles(): void {
        const particles = document.createElement('div');
        particles.id = 'particles';
        particles.className = 'fixed inset-0 pointer-events-none z-0';
        document.body.appendChild(particles);

        // Create floating particles
        for (let i = 0; i < 50; i++) {
            const particle = document.createElement('div');
            particle.className = 'absolute w-1 h-1 bg-black rounded-full opacity-10';
            particle.style.left = `${Math.random() * 100}vw`;
            particle.style.top = `${Math.random() * 100}vh`;
            particles.appendChild(particle);

            gsap.to(particle, {
                y: 'random(-100, 100)',
                x: 'random(-100, 100)',
                opacity: 'random(0.05, 0.15)',
                duration: 'random(2, 4)',
                repeat: -1,
                yoyo: true,
                ease: 'sine.inOut'
            });
        }
    }

    private render(): void {
        const state = this.gameService.getCurrentState();
        const question = this.gameService.getCurrentQuestion();
        const content = this.gameService.getCurrentContent();

        this.container.innerHTML = `
            <div class="min-h-screen bg-gradient-to-br from-valentine-navy to-valentine-deep-red p-4 sm:p-8">
                <div class="w-[95%] max-w-7xl mx-auto bg-white/95 backdrop-blur-sm p-8 sm:p-12 shadow-lg border border-valentine-gold/10">
                    <header class="text-center mb-12">
                        <h1 class="text-3xl sm:text-4xl font-display font-light text-valentine-burgundy tracking-wide uppercase">Our Story</h1>
                        <p class="text-sm font-elegant text-valentine-charcoal/80 mt-2 tracking-widest uppercase">Chapter ${state.currentSection} · Scene ${state.currentLevel}</p>
                    </header>
                    <div id="game-content" class="space-y-8">
                        ${this.renderCurrentQuestion(question)}
                    </div>
                </div>
            </div>
        `;

        // Initialize typing effect if needed
        const typingText = document.querySelector('.typing-text');
        if (typingText && typingText.hasAttribute('data-text')) {
            const text = typingText.getAttribute('data-text') || '';
            this.initializeTypingEffect(typingText as HTMLElement, text);
        }

        // Initialize YouTube player for both quiz and transition content
        const mediaContainer = document.getElementById('media-container');
        if (mediaContainer) {
            let youtubeClip: YouTubeClip | undefined;
            
            if (content?.type === 'TRANSITION' && content.media?.type === 'YOUTUBE') {
                youtubeClip = content.media as YouTubeClip;
            } else if (question?.media?.type === 'YOUTUBE') {
                youtubeClip = question.media as YouTubeClip;
            }

            if (youtubeClip) {
                console.log('Initializing YouTube player with clip:', youtubeClip);
                this.youtubePlayer = new YouTubePlayer(
                    'media-container',
                    youtubeClip,
                    this.handleYouTubeStateChange.bind(this)
                );
            }
        }

        // Animate content in
        const gameContent = document.getElementById('game-content');
        if (gameContent) {
            gsap.from(gameContent, {
                opacity: 0,
                y: 20,
                duration: 0.5,
                ease: 'back.out(1.7)'
            });
        }
    }

    private renderCurrentQuestion(question: Question | null): string {
        const content = this.gameService.getCurrentContent();
        if (!content) {
            return this.renderGameComplete();
        }

        switch (content.type) {
            case 'MEMORY':
                return this.renderMemoryContent(content);
            case 'TRANSITION':
                return this.renderTransitionContent(content);
            case 'QUIZ':
                if (!question) {
                    return this.renderGameComplete();
                }
                return this.renderQuizContent(question);
            default:
                return this.renderGameComplete();
        }
    }

    private renderMemoryContent(content: MemoryContent): string {
        return `
            <div class="text-center">
                <h2 class="text-2xl font-display mb-4 text-valentine-charcoal">${content.title}</h2>
                <p class="text-lg mb-8 text-valentine-charcoal/80">${content.description}</p>
                <div class="memory-stream grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    ${content.media.map((media: MediaContent, index: number) => {
                        if (media.type === 'IMAGE') {
                            return `
                                <div class="memory-item aspect-square overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                                    <img 
                                        src="${media.url}" 
                                        alt="Memory ${index + 1}"
                                        class="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                    />
                                </div>
                            `;
                        } else if (media.type === 'VIDEO') {
                            return `
                                <div class="memory-item aspect-square overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
                                    <video 
                                        src="${media.url}"
                                        class="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                        controls
                                        muted
                                        loop
                                        playsinline
                                        preload="auto"
                                        onerror="console.error('Error loading video:', this.src)"
                                    ></video>
                                </div>
                            `;
                        }
                        return '';
                    }).join('')}
                </div>
                <div class="flex justify-center gap-4">
                    <button 
                        class="px-8 py-3 transition-all bg-valentine-burgundy text-white hover:bg-valentine-deep-red uppercase tracking-widest font-light shadow-lg hover:shadow-xl"
                        data-action="continue"
                        data-prompt="${content.continuePrompt}"
                    >
                        ${content.continueText}
                    </button>
                    <button 
                        class="px-8 py-3 transition-all bg-valentine-burgundy/20 text-valentine-burgundy hover:bg-valentine-burgundy/30 uppercase tracking-widest font-light shadow-lg hover:shadow-xl"
                        data-action="replay"
                        data-prompt="${content.replayPrompt}"
                    >
                        ${content.replayText}
                    </button>
                </div>
            </div>
        `;
    }

    private renderTransitionContent(content: TransitionContent): string {
        let mediaHtml = '';
        if (content.media?.type === 'YOUTUBE') {
            mediaHtml = `
                <div class="mb-6">
                    <div id="media-container" class="video-container aspect-video w-full max-w-2xl mx-auto overflow-hidden"></div>
                </div>
            `;
        } else if (content.media?.type === 'IMAGE') {
            mediaHtml = `
                <div class="mb-6">
                    <img 
                        src="${content.media.url}" 
                        alt="${content.media.title}"
                        title="${content.media.description || ''}"
                        class="max-w-2xl mx-auto shadow-lg hover:shadow-xl transition-shadow duration-300"
                    />
                </div>
            `;
        }

        return `
            <div class="text-center">
                <h2 class="text-2xl font-display mb-4 text-valentine-charcoal">${content.title}</h2>
                ${mediaHtml}
                <p class="text-lg mb-8 text-valentine-charcoal/80">${content.text}</p>
                <div class="flex justify-center gap-4">
                    <button 
                        class="px-8 py-3 transition-all bg-valentine-burgundy text-white hover:bg-valentine-deep-red uppercase tracking-widest font-light shadow-lg hover:shadow-xl"
                        data-action="continue"
                        data-prompt="${content.continuePrompt}"
                    >
                        ${content.continueText}
                    </button>
                    <button 
                        class="px-8 py-3 transition-all bg-valentine-burgundy/20 text-valentine-burgundy hover:bg-valentine-burgundy/30 uppercase tracking-widest font-light shadow-lg hover:shadow-xl"
                        data-action="replay"
                        data-prompt="${content.replayPrompt}"
                    >
                        ${content.replayText}
                    </button>
                </div>
            </div>
        `;
    }

    private renderQuizContent(question: Question): string {
        let mediaHtml = '';
        console.log('Rendering quiz content with media:', question.media);
        
        if (question.media?.type === 'YOUTUBE') {
            mediaHtml = `
                <div class="mb-6">
                    <div id="media-container" class="video-container aspect-video w-full max-w-2xl mx-auto overflow-hidden"></div>
                </div>
            `;
        } else if (question.media?.type === 'IMAGE') {
            mediaHtml = `
                <div class="mb-6">
                    <img 
                        src="${question.media.url}" 
                        alt="${question.media.title}"
                        title="${question.media.description || ''}"
                        class="max-w-2xl mx-auto shadow-lg hover:shadow-xl transition-shadow duration-300"
                    />
                </div>
            `;
        } else if (question.media?.type === 'VIDEO') {
            console.log('Rendering video with URL:', question.media.url);
            mediaHtml = `
                <div class="mb-6">
                    <video 
                        src="${question.media.url}"
                        class="w-full max-w-2xl mx-auto shadow-lg hover:shadow-xl transition-shadow duration-300"
                        controls
                        muted
                        loop
                        playsinline
                        preload="auto"
                        onloadstart="console.log('Video load started:', this.src)"
                        onloadeddata="console.log('Video data loaded:', this.src)"
                        onerror="console.error('Error loading video:', this.src)"
                    ></video>
                </div>
            `;
        }

        let contentHtml = '';
        switch (question.type) {
            case 'MULTIPLE_CHOICE':
                contentHtml = this.renderMultipleChoice(question);
                break;
            case 'TEXT':
            case 'FILL_BLANK':
                contentHtml = this.renderTextInput();
                break;
            case 'DRAG_DROP':
                contentHtml = this.renderDragDrop(question);
                break;
            case 'UNSCRAMBLE':
                contentHtml = this.renderUnscramble(question);
                break;
        }

        const shouldType = question.text.includes('<type>');
        const questionText = question.text.replace('<type>', '');

        return `
            <div class="text-center">
                <h2 class="text-2xl font-display mb-4 text-valentine-charcoal ${shouldType ? 'typing-text' : ''}" ${shouldType ? 'data-text="' + questionText + '"' : ''}>${shouldType ? '' : questionText}</h2>
                ${mediaHtml}
                <div class="w-full max-w-4xl mx-auto">
                    ${contentHtml}
                </div>
                ${question.hint ? `
                    <button class="mt-4 text-sm text-valentine-burgundy hover:text-valentine-deep-red transition-colors" onclick="window.showHint()">
                        Need a hint? 💭
                    </button>
                ` : ''}
            </div>
        `;
    }

    private renderGameComplete(): string {
        return `
            <div class="text-center">
                <h2 class="text-3xl font-display font-light text-valentine-burgundy mb-4 tracking-wide uppercase animate-glow">Journey Complete</h2>
                <p class="text-valentine-charcoal/80 mb-8 tracking-wider font-elegant">The end is just another beginning</p>
                <div class="mt-8">
                    <button class="px-12 py-3 rounded-none transition-all bg-valentine-burgundy text-white hover:bg-valentine-deep-red uppercase tracking-widest font-light shadow-lg hover:shadow-xl" onclick="restartGame()">
                        Again
                    </button>
                </div>
            </div>
        `;
    }

    private renderMultipleChoice(question: Question): string {
        return question.options?.map((option: string) => `
            <button 
                class="w-full md:w-3/4 mx-auto block mb-3 text-left p-3 transition-all hover:bg-valentine-navy/5 border border-valentine-gold/20 hover:border-valentine-burgundy text-valentine-charcoal tracking-wide text-base font-body ${option === 'ⁿᵒ' ? 'text-[8px]' : ''}"
                data-answer="${option}"
            >
                ${option}
            </button>
        `).join('') || '';
    }

    private renderTextInput(): string {
        console.log(`Rendering text input`);
        return `
            <div class="space-y-4 max-w-2xl mx-auto">
                <input 
                    type="text" 
                    class="w-full p-3 border border-valentine-gold/20 focus:border-valentine-burgundy outline-none transition-all tracking-wide text-valentine-charcoal text-base font-body"
                    placeholder="Your answer..."
                    data-input="true"
                />
                <button 
                    class="w-48 mx-auto block py-2 px-4 transition-all bg-valentine-burgundy text-white hover:bg-valentine-deep-red uppercase tracking-wide text-base font-light shadow-lg hover:shadow-xl"
                    data-submit="true"
                >
                    Submit
                </button>
            </div>
        `;
    }

    private renderDragDrop(question: Question): string {
        if (!question.dragDropConfig) return '';

        const { items, slots } = question.dragDropConfig;
        
        return `
            <div class="space-y-8 max-w-2xl mx-auto">
                <div class="flex flex-wrap gap-4 justify-center" id="drag-items">
                    ${items.map((item) => `
                        <div 
                            class="p-3 bg-valentine-navy/5 border border-valentine-gold/20 cursor-move transition-transform text-valentine-charcoal hover:border-valentine-burgundy text-base font-body"
                            draggable="true"
                            data-drag-item="${item}"
                        >
                            ${item}
                        </div>
                    `).join('')}
                </div>
                <div class="space-y-4" id="drop-slots">
                    ${slots.map((slot, i) => `
                        <div 
                            class="p-3 border-2 border-dashed border-valentine-gold/30 min-h-[50px] transition-all hover:border-valentine-burgundy/50"
                            data-drop-slot="${i}"
                        >
                            <span class="text-valentine-charcoal/60 text-base font-body">${slot}</span>
                        </div>
                    `).join('')}
                </div>
                <button 
                    class="w-48 mx-auto block py-2 px-4 transition-all bg-valentine-burgundy text-white hover:bg-valentine-deep-red uppercase tracking-wide text-base font-light shadow-lg hover:shadow-xl"
                    data-check-order="true"
                >
                    Verify Order
                </button>
            </div>
        `;
    }

    private renderUnscramble(question: Question): string {
        if (!question.unscrambleConfig) return '';

        const { letters } = question.unscrambleConfig;
        
        return `
            <div class="space-y-8 max-w-2xl mx-auto">
                <div class="flex flex-wrap gap-4 justify-center" id="letter-container">
                    ${letters.map((letter) => `
                        <div 
                            class="p-3 bg-valentine-navy/5 border border-valentine-gold/20 cursor-move transition-transform text-valentine-charcoal hover:border-valentine-burgundy text-base font-body"
                            draggable="true"
                            data-letter="${letter}"
                        >
                            ${letter}
                        </div>
                    `).join('')}
                </div>
                <div class="p-3 border-2 border-dashed border-valentine-gold/30 min-h-[50px] transition-all hover:border-valentine-burgundy/50" id="answer-container">
                    <span class="text-valentine-charcoal/60 text-base font-body">Arrange the words here...</span>
                </div>
                <button 
                    class="w-48 mx-auto block py-2 px-4 transition-all bg-valentine-burgundy text-white hover:bg-valentine-deep-red uppercase tracking-wide text-base font-light shadow-lg hover:shadow-xl"
                    data-check-answer="true"
                >
                    Verify
                </button>
            </div>
        `;
    }

    private setupEventListeners(): void {
        this.container.addEventListener('click', this.handleClick.bind(this));
        this.setupDragAndDrop();
        (window as any).showHint = this.showHint.bind(this);
    }

    private handleClick(e: Event): void {
        const target = e.target as HTMLElement;
        
        if (target.hasAttribute('data-answer')) {
            this.handleMultipleChoiceAnswer(target);
        } else if (target.hasAttribute('data-submit')) {
            this.handleTextSubmit();
        } else if (target.hasAttribute('data-check-order')) {
            this.handleDragDropSubmit();
        } else if (target.hasAttribute('data-check-answer')) {
            this.handleUnscrambleSubmit();
        } else if (target.hasAttribute('data-action')) {
            const action = target.getAttribute('data-action');
            const prompt = target.getAttribute('data-prompt');
            
            if (action === 'continue') {
                this.handleContinue(prompt || 'Ready to continue?');
            } else if (action === 'replay') {
                this.handleReplay();
            }
        }
    }

    private async handleMultipleChoiceAnswer(target: HTMLElement): Promise<void> {
        const answer = target.getAttribute('data-answer') || '';
        const question = this.gameService.getCurrentQuestion();
        if (question) {
            const isCorrect = await this.gameService.submitAnswer(question.id, answer);
            this.handleAnswerResult(isCorrect, question);
        }
    }

    private async handleTextSubmit(): Promise<void> {
        const input = this.container.querySelector('[data-input]') as HTMLInputElement;
        if (!input) return;

        const answer = input.value.trim();
        const question = this.gameService.getCurrentQuestion();
        if (question) {
            const isCorrect = await this.gameService.submitAnswer(question.id, answer);
            this.handleAnswerResult(isCorrect, question);
        }
    }

    private async handleDragDropSubmit(): Promise<void> {
        const slots = Array.from(this.container.querySelectorAll('[data-drop-slot]'));
        const order = slots.map(slot => {
            const item = slot.querySelector('[data-drag-item]');
            return item ? item.getAttribute('data-drag-item') : '';
        }).filter(Boolean);

        const question = this.gameService.getCurrentQuestion();
        if (question && order.length === question.dragDropConfig?.items.length) {
            const isCorrect = await this.gameService.submitAnswer(question.id, JSON.stringify(order));
            this.handleAnswerResult(isCorrect, question);
        }
    }

    private async handleUnscrambleSubmit(): Promise<void> {
        const container = document.getElementById('answer-container');
        if (!container) return;

        const letters = Array.from(container.querySelectorAll('[data-letter]'))
            .map(el => el.getAttribute('data-letter'))
            .filter(Boolean);

        const answer = letters.join(' ');
        const question = this.gameService.getCurrentQuestion();
        if (question) {
            const isCorrect = await this.gameService.submitAnswer(question.id, answer);
            this.handleAnswerResult(isCorrect, question);
        }
    }

    private setupDragAndDrop(): void {
        this.container.addEventListener('dragstart', (e: DragEvent) => {
            const target = e.target as HTMLElement;
            if (target.hasAttribute('data-drag-item') || target.hasAttribute('data-letter')) {
                this.dragState.dragging = target;
                target.classList.add('opacity-50');
                if (e.dataTransfer) {
                    e.dataTransfer.effectAllowed = 'move';
                }
            }
        });

        this.container.addEventListener('dragend', () => {
            if (this.dragState.dragging) {
                this.dragState.dragging.classList.remove('opacity-50');
                this.dragState.dragging = null;
            }
        });

        this.container.addEventListener('dragover', (e: DragEvent) => {
            e.preventDefault();
            if (e.dataTransfer) {
                e.dataTransfer.dropEffect = 'move';
            }
            const target = e.target as HTMLElement;
            const dropZone = target.closest('[data-drop-slot], #answer-container');
            if (dropZone) {
                dropZone.classList.add('border-black', 'bg-gray-50');
            }
        });

        this.container.addEventListener('dragleave', (e: DragEvent) => {
            const target = e.target as HTMLElement;
            const dropZone = target.closest('[data-drop-slot], #answer-container');
            if (dropZone) {
                dropZone.classList.remove('border-black', 'bg-gray-50');
            }
        });

        this.container.addEventListener('drop', (e: DragEvent) => {
            e.preventDefault();
            const target = e.target as HTMLElement;
            const dropZone = target.closest('[data-drop-slot], #answer-container');
            
            if (dropZone && this.dragState.dragging) {
                dropZone.classList.remove('border-black', 'bg-gray-50');
                
                // Handle existing item in drop zone
                const existingItem = dropZone.querySelector('[data-drag-item], [data-letter]');
                const dragItemsContainer = document.getElementById('drag-items');
                const answerContainer = document.getElementById('answer-container');
                
                // If there's an existing item and it's not the same as what we're dragging
                if (existingItem && existingItem !== this.dragState.dragging) {
                    // If we're dragging from drag-items, return existing item there
                    if (dragItemsContainer && this.dragState.dragging.parentElement === dragItemsContainer) {
                        dragItemsContainer.appendChild(existingItem);
                    }
                    // If we're dragging from answer-container, return existing item there
                    else if (answerContainer && this.dragState.dragging.parentElement === answerContainer) {
                        answerContainer.appendChild(existingItem);
                    }
                }

                // Clear placeholder text if it exists
                if (dropZone.querySelector('span')) {
                    dropZone.innerHTML = '';
                }

                // Move the dragged item to the new location
                dropZone.appendChild(this.dragState.dragging);
                
                // Animate the placement
                gsap.from(this.dragState.dragging, {
                    scale: 0.8,
                    opacity: 0,
                    duration: 0.3,
                    ease: 'back.out(1.7)'
                });
            }
        });
    }

    private async handleAnswerResult(isCorrect: boolean, question: Question): Promise<void> {
        const feedback = isCorrect ? question.feedback.correct : question.feedback.incorrect;
        
        // Create and animate feedback element
        const feedbackEl = document.createElement('div');
        feedbackEl.className = `fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
            bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-2xl z-50 ${isCorrect ? 'text-valentine-burgundy' : 'text-valentine-deep-red'} border border-valentine-gold/10`;
        feedbackEl.textContent = feedback;
        document.body.appendChild(feedbackEl);

        // Animate feedback
        gsap.fromTo(feedbackEl,
            { opacity: 0, scale: 0.8, y: 20 },
            { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'back.out(1.7)' }
        );

        // If correct, show celebration particles and play audio if available
        if (isCorrect) {
            this.showCelebration();
            if (this.youtubePlayer && question.media?.type === 'YOUTUBE') {
                this.youtubePlayer.playCorrectAnswerAudio();
            }
        }

        // Remove feedback after delay
        setTimeout(async () => {
            gsap.to(feedbackEl, {
                opacity: 0,
                scale: 0.8,
                y: 20,
                duration: 0.5,
                ease: 'back.in(1.7)',
                onComplete: () => {
                    feedbackEl.remove();
                    if (isCorrect) {
                        this.progressToNext();
                    }
                }
            });
        }, 2000);
    }

    private showCelebration(): void {
        // Create celebration particles
        const container = document.createElement('div');
        container.className = 'fixed inset-0 pointer-events-none z-40';
        document.body.appendChild(container);

        for (let i = 0; i < 50; i++) {
            const particle = document.createElement('div');
            particle.className = 'absolute w-2 h-2 rounded-full';
            particle.style.backgroundColor = [
                '#800020', // burgundy
                '#D4AF37', // gold
                '#C41E3A', // accent
                '#B87333'  // copper
            ][Math.floor(Math.random() * 4)];
            particle.style.left = '50%';
            particle.style.top = '50%';

            container.appendChild(particle);

            gsap.to(particle, {
                x: `random(-300, 300)`,
                y: `random(-300, 300)`,
                scale: `random(0.5, 1.5)`,
                duration: 'random(1, 2)',
                ease: 'power2.out',
                opacity: 0,
                onComplete: () => particle.remove()
            });
        }

        setTimeout(() => container.remove(), 2000);
    }

    private async progressToNext(): Promise<void> {
        if (this.youtubePlayer) {
            this.youtubePlayer.destroy();
            this.youtubePlayer = null;
        }

        await this.gameService.progressToNextLevel();
        this.animateTransition();
    }

    private handleYouTubeStateChange(state: number): void {
        console.log('YouTube state changed:', state);
    }

    private animateTransition(): void {
        const content = document.getElementById('game-content');
        if (content) {
            gsap.to(content, {
                opacity: 0,
                scale: 0.8,
                y: 20,
                duration: 0.5,
                ease: 'back.in(1.7)',
                onComplete: () => {
                    this.render();
                    const newContent = document.getElementById('game-content');
                    if (newContent) {
                        gsap.fromTo(newContent,
                            { opacity: 0, scale: 0.8, y: 20 },
                            { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'back.out(1.7)' }
                        );
                    }
                }
            });
        }
    }

    private showHint(): void {
        const question = this.gameService.getCurrentQuestion();
        if (!question?.hint) return;

        // Create hint element
        const hintEl = document.createElement('div');
        hintEl.className = 'fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/95 backdrop-blur-sm p-8 shadow-xl z-50 text-valentine-charcoal max-w-md border border-valentine-gold/10';
        hintEl.innerHTML = `
            <div class="text-sm font-elegant font-light tracking-widest uppercase mb-4 text-valentine-burgundy">Reminder</div>
            <p class="text-valentine-charcoal/80 tracking-wide font-body">${question.hint}</p>
            <button class="mt-6 text-sm text-valentine-burgundy hover:text-valentine-deep-red uppercase tracking-wider font-light" onclick="this.parentElement.remove()">
                Close
            </button>
        `;
        document.body.appendChild(hintEl);

        // Animate hint
        gsap.fromTo(hintEl,
            { opacity: 0, y: 20 },
            { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }
        );
    }

    private async handleContinue(prompt: string): Promise<void> {
        // Create and animate prompt element
        const promptEl = document.createElement('div');
        promptEl.className = `fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
            bg-white/95 backdrop-blur-sm p-6 rounded-xl shadow-2xl z-50 text-valentine-burgundy border border-valentine-gold/10`;
        promptEl.textContent = prompt;
        document.body.appendChild(promptEl);

        // Animate prompt
        gsap.fromTo(promptEl,
            { opacity: 0, scale: 0.8, y: 20 },
            { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: 'back.out(1.7)' }
        );

        // Remove prompt after delay and progress
        setTimeout(async () => {
            gsap.to(promptEl, {
                opacity: 0,
                scale: 0.8,
                y: 20,
                duration: 0.5,
                ease: 'back.in(1.7)',
                onComplete: () => {
                    promptEl.remove();
                    this.progressToNext();
                }
            });
        }, 2000);
    }

    private handleReplay(): void {
        const content = this.gameService.getCurrentContent();
        if (!content) return;

        if (content.type === 'MEMORY') {
            // For memory content, scroll back to top smoothly
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (content.type === 'TRANSITION' && this.youtubePlayer) {
            // For transition content with video, replay the video
            this.youtubePlayer.seekTo(0);
            this.youtubePlayer.play();
        }
    }

    private initializeTypingEffect(element: HTMLElement, text: string): void {
        let currentChar = 0;
        const typingDelay = 100; // milliseconds per character

        const type = () => {
            if (currentChar < text.length) {
                element.textContent = text.slice(0, currentChar + 1);
                currentChar++;
                setTimeout(type, typingDelay);
            }
        };

        // Start typing effect
        setTimeout(type, 500); // Initial delay before starting
    }
}